'use client';

import { c as _c } from "react/compiler-runtime";
import { ChainId } from '@morpho-org/blue-sdk';
import { InterfaceToken, MORPHO_TOKEN_FAQ_URL, ZERO } from '@morpho-org/dapp-core';
import { Avatar } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { Close } from '@repo/ui/icons';
import { erc20Abi, isAddressEqual } from 'viem';
import { useReadContract, useWalletClient } from 'wagmi';
import WrapMorphoLegacyRecap from '@/components/Bundler/ConfirmDialog/Recaps/WrapMorphoLegacyRecap';
import { ButtonLink } from '@/components/common/ButtonLink';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useMorphoLegacyContext } from '@/contexts/MorphoLegacy/MorphoLegacy.context';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { mainnetAddresses } from '@/helpers/addresses';
import { getWrapMorphoV2Transaction } from '@/helpers/wrap/getWrapMorphoV2Transaction';
import { useChainNavigation } from '@/hooks/useChainNavigation';
import { logger } from '@/lib/observability/logger';

// Legacy MORPHO only exists on Ethereum mainnet: pin the whole wrap flow to it.
const WRAP_CHAIN_ID = ChainId.EthMainnet;
export function WrapLegacyMorphoDialog() {
  const $ = _c(65);
  const {
    toggleDialog
  } = useDialogContext();
  const {
    targetChainId,
    targetChain,
    account,
    switchToTargetChain
  } = useWeb3Context();
  const {
    data: client
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    legacyMorphoHolding,
    morphoHolding
  } = useMorphoLegacyContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      chainId: WRAP_CHAIN_ID
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    syncChainSwitch
  } = useChainNavigation(t0);
  let t1;
  if ($[1] !== switchToTargetChain || $[2] !== syncChainSwitch || $[3] !== targetChainId) {
    t1 = () => {
      if (targetChainId !== WRAP_CHAIN_ID) {
        syncChainSwitch();
      } else {
        switchToTargetChain();
      }
    };
    $[1] = switchToTargetChain;
    $[2] = syncChainSwitch;
    $[3] = targetChainId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const switchToEthereum = t1;
  const isWrongChain = !!account.address && (targetChainId !== WRAP_CHAIN_ID || account.chain?.id !== WRAP_CHAIN_ID);
  let t2;
  if ($[5] !== account.address || $[6] !== client) {
    t2 = !!client && client.chain?.id === WRAP_CHAIN_ID && !!account.address && isAddressEqual(client.account.address, account.address);
    $[5] = account.address;
    $[6] = client;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isWalletReady = t2;
  const t3 = !!account.address;
  let t4;
  if ($[8] !== t3) {
    t4 = {
      enabled: t3
    };
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== account.address) {
    t5 = account.address ? [account.address, mainnetAddresses.morphoTokenWrapper] : undefined;
    $[10] = account.address;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = {
      query: t4,
      chainId: WRAP_CHAIN_ID,
      abi: erc20Abi,
      address: mainnetAddresses.legacyMorphoToken,
      functionName: "allowance",
      args: t5
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    data: wrapperAllowance,
    isFetching: isWrapperAllowanceFetching
  } = useReadContract(t6);
  let t7;
  if ($[15] !== legacyMorphoHolding.value) {
    t7 = formatters.short.digits(2).min(0.01).of(legacyMorphoHolding.value ?? ZERO, 18);
    $[15] = legacyMorphoHolding.value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const formattedLegacyMorpho = t7;
  let t8;
  if ($[17] !== morphoHolding.value) {
    t8 = formatters.short.digits(2).min(0.01).of(morphoHolding.value ?? ZERO, 18);
    $[17] = morphoHolding.value;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const formattedMorpho = t8;
  let t9;
  if ($[19] !== account.address || $[20] !== isWrapperAllowanceFetching || $[21] !== legacyMorphoHolding.value || $[22] !== wrapperAllowance) {
    t9 = !account.address || !legacyMorphoHolding.value || wrapperAllowance === undefined || isWrapperAllowanceFetching ? undefined : getWrapMorphoV2Transaction({
      amount: legacyMorphoHolding.value,
      userAddress: account.address,
      wrapperAllowance
    });
    $[19] = account.address;
    $[20] = isWrapperAllowanceFetching;
    $[21] = legacyMorphoHolding.value;
    $[22] = wrapperAllowance;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const wrapTransaction = t9;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new InterfaceToken({
      address: mainnetAddresses.legacyMorphoToken,
      symbol: "MORPHO",
      name: "Morpho Token",
      decimals: 18,
      chain: {
        id: WRAP_CHAIN_ID
      },
      price: undefined,
      logoURI: "https://cdn.morpho.org/assets/logos/morpho.svg"
    });
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const legacyMorphoToken = t10;
  let t11;
  if ($[25] !== account || $[26] !== client || $[27] !== isWalletReady || $[28] !== legacyMorphoHolding || $[29] !== morphoHolding || $[30] !== submitTransactions || $[31] !== targetChain || $[32] !== toggleDialog || $[33] !== wrapTransaction) {
    t11 = () => {
      if (!wrapTransaction || !client || !isWalletReady) {
        return;
      }
      submitTransactions(client, {
        actionType: "wrapLegacyMorpho",
        flow: "wrap",
        skipFinalSimulation: true,
        transaction: wrapTransaction.transaction,
        requirements: wrapTransaction.requirements,
        onTxSuccess: () => {
          legacyMorphoHolding.refresh(true);
          morphoHolding.refresh(true);
        },
        chain: targetChain,
        account
      }).catch(error => {
        logger.error(error instanceof Error ? error : new Error(String(error)), {
          tags: {
            component: "WrapLegacyMorphoDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage="Wrap your legacy MORPHO" token={legacyMorphoToken} operationContent={<WrapMorphoLegacyRecap mode="Confirm" />} />, {
        closable: false
      });
    };
    $[25] = account;
    $[26] = client;
    $[27] = isWalletReady;
    $[28] = legacyMorphoHolding;
    $[29] = morphoHolding;
    $[30] = submitTransactions;
    $[31] = targetChain;
    $[32] = toggleDialog;
    $[33] = wrapTransaction;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const execute = t11;
  let t12;
  if ($[35] !== toggleDialog) {
    t12 = () => toggleDialog(false);
    $[35] = toggleDialog;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Close />;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t12) {
    t14 = <Button className="ml-auto" variant="secondary" size="icon" aria-label="Close" onClick={t12}>{t13}</Button>;
    $[38] = t12;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Avatar src="https://cdn.morpho.org/assets/logos/morpho.svg" size={40} />;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-title-sm" style={{
      textAlign: "center"
    }}>Wrap your legacy MORPHO</span>;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="text-md" style={{
      textAlign: "center"
    }}>The Morpho DAO has enabled transferability for the MORPHO token!</span>;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="text-md" style={{
      textAlign: "center"
    }}>Holders of legacy MORPHO tokens on Ethereum will need to wrap their tokens to enable transferability.</span>;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== execute || $[45] !== isWalletReady || $[46] !== isWrongChain || $[47] !== switchToEthereum || $[48] !== wrapTransaction) {
    t19 = isWrongChain ? <Button size="lg" onClick={switchToEthereum}>Switch to Ethereum</Button> : <Button size="lg" onClick={execute} disabled={!isWalletReady || !wrapTransaction}>Wrap</Button>;
    $[44] = execute;
    $[45] = isWalletReady;
    $[46] = isWrongChain;
    $[47] = switchToEthereum;
    $[48] = wrapTransaction;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ButtonLink href={MORPHO_TOKEN_FAQ_URL} buttonProps={{
      variant: "secondary",
      size: "sm",
      label: "FAQ"
    }} />;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t19) {
    t21 = <>{t16}{t17}{t18}<div className="gap-sm flex w-full flex-col items-center">{t19}{t20}</div></>;
    $[51] = t19;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="text-xs">Wallet Balance</span>;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const t23 = `Legacy MORPHO: ${formattedLegacyMorpho} MORPHO`;
  let t24;
  if ($[54] !== t23) {
    t24 = <span className="text-xs">{t23}</span>;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  const t25 = `Wrapped MORPHO: ${formattedMorpho} MORPHO`;
  let t26;
  if ($[56] !== t25) {
    t26 = <span className="text-xs">{t25}</span>;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t24 || $[59] !== t26) {
    t27 = <div className="p-md bg-muted flex w-full flex-col items-center gap-[6px] rounded-[10px]">{t22}{t24}{t26}</div>;
    $[58] = t24;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t14 || $[62] !== t21 || $[63] !== t27) {
    t28 = <div className="flex flex-col items-center justify-center gap-[24px]">{t14}{t15}{t21}{t27}</div>;
    $[61] = t14;
    $[62] = t21;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  return t28;
}